import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { type ApprovedTheme } from './approved-theme';
import { APPROVED_THEMES, DEFAULT_THEME } from './approved-themes';

const themeProperties = {
  '--app-primary': (theme: ApprovedTheme) => theme.tokens.color.primary,
  '--app-primary-container': (theme: ApprovedTheme) => theme.tokens.color.primaryContainer,
  '--app-secondary': (theme: ApprovedTheme) => theme.tokens.color.secondary,
  '--app-background': (theme: ApprovedTheme) => theme.tokens.color.background,
  '--app-surface': (theme: ApprovedTheme) => theme.tokens.color.surface,
  '--app-surface-variant': (theme: ApprovedTheme) => theme.tokens.color.surfaceVariant,
  '--app-text-primary': (theme: ApprovedTheme) => theme.tokens.color.textPrimary,
  '--app-text-secondary': (theme: ApprovedTheme) => theme.tokens.color.textSecondary,
  '--app-border': (theme: ApprovedTheme) => theme.tokens.color.border,
  '--app-disabled': (theme: ApprovedTheme) => theme.tokens.color.disabled,
  '--app-success': (theme: ApprovedTheme) => theme.tokens.color.success,
  '--app-warning': (theme: ApprovedTheme) => theme.tokens.color.warning,
  '--app-error': (theme: ApprovedTheme) => theme.tokens.color.error,
  '--app-info': (theme: ApprovedTheme) => theme.tokens.color.info,
  '--app-font-family': (theme: ApprovedTheme) => theme.tokens.typography.fontFamily,
  '--app-font-size-base': (theme: ApprovedTheme) => theme.tokens.typography.fontSizeBase,
  '--app-font-size-small': (theme: ApprovedTheme) => theme.tokens.typography.fontSizeSmall,
  '--app-font-size-large': (theme: ApprovedTheme) => theme.tokens.typography.fontSizeLarge,
  '--app-font-weight-regular': (theme: ApprovedTheme) => theme.tokens.typography.fontWeightRegular,
  '--app-font-weight-medium': (theme: ApprovedTheme) => theme.tokens.typography.fontWeightMedium,
  '--app-font-weight-bold': (theme: ApprovedTheme) => theme.tokens.typography.fontWeightBold,
  '--app-line-height-base': (theme: ApprovedTheme) => theme.tokens.typography.lineHeightBase,
  '--app-spacing-xs': (theme: ApprovedTheme) => theme.tokens.spacing.xs,
  '--app-spacing-sm': (theme: ApprovedTheme) => theme.tokens.spacing.sm,
  '--app-spacing-md': (theme: ApprovedTheme) => theme.tokens.spacing.md,
  '--app-spacing-lg': (theme: ApprovedTheme) => theme.tokens.spacing.lg,
  '--app-spacing-xl': (theme: ApprovedTheme) => theme.tokens.spacing.xl,
  '--app-radius-sm': (theme: ApprovedTheme) => theme.tokens.radius.sm,
  '--app-radius-md': (theme: ApprovedTheme) => theme.tokens.radius.md,
  '--app-radius-lg': (theme: ApprovedTheme) => theme.tokens.radius.lg,
  '--app-radius-pill': (theme: ApprovedTheme) => theme.tokens.radius.pill,
  '--app-elevation-sm': (theme: ApprovedTheme) => theme.tokens.elevation.sm,
  '--app-elevation-md': (theme: ApprovedTheme) => theme.tokens.elevation.md,
  '--app-elevation-lg': (theme: ApprovedTheme) => theme.tokens.elevation.lg,
} as const;

export interface ThemeDevTools {
  readonly themes: readonly string[];
  activeTheme(): string;
  setTheme(themeId: string): string;
}

declare global {
  interface Window {
    __preNominaTheme?: ThemeDevTools;
  }
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly activeThemeState = signal<ApprovedTheme>(DEFAULT_THEME);

  readonly themes: readonly ApprovedTheme[] = APPROVED_THEMES;
  readonly activeTheme = this.activeThemeState.asReadonly();

  initialize(): void {
    this.applyTheme(DEFAULT_THEME);
    this.exposeDevelopmentTools();
  }

  getTheme(themeId: string): ApprovedTheme | undefined {
    return this.themes.find((theme) => theme.id === themeId);
  }

  setTheme(themeId: string): ApprovedTheme {
    const theme = this.getTheme(themeId) ?? DEFAULT_THEME;
    this.applyTheme(theme);
    return theme;
  }

  private applyTheme(theme: ApprovedTheme): void {
    const root = this.document.documentElement;
    root.dataset['appTheme'] = theme.id;
    root.style.colorScheme = theme.colorScheme;

    for (const [property, resolveValue] of Object.entries(themeProperties)) {
      root.style.setProperty(property, resolveValue(theme));
    }

    this.activeThemeState.set(theme);
  }

  private exposeDevelopmentTools(): void {
    const browserWindow = this.document.defaultView;
    if (environment.production || browserWindow === null) {
      return;
    }

    Object.defineProperty(browserWindow, '__preNominaTheme', {
      configurable: true,
      value: Object.freeze({
        themes: this.themes.map((theme) => theme.id),
        activeTheme: () => this.activeTheme().id,
        setTheme: (themeId: string) => this.setTheme(themeId).id,
      } satisfies ThemeDevTools),
    });
  }
}
